import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { MapPin, Building, Store, Home } from "lucide-react";
import { fileUrl } from "../../api/fileUrl";
import { localName } from "../../lib/localName";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const ENTITY_META = {
  subsidiary: { icon: Building, color: "bg-indigo-600" },
  branch: { icon: Store, color: "bg-fuchsia-500" },
};

// بطاقات فروع ثلاثية الأبعاد بأيقونة ملوّنة مطابقة لنمط الرئيسية.
export const BranchList = ({ branches }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (branches.length === 0) return <p className="text-sm text-muted-foreground">{t("app.none")}</p>;

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="branch-list">
      {branches.map((b, idx) => {
        const meta = ENTITY_META[b.entity_type || "branch"];
        const EntityIcon = meta.icon;
        return (
          <button
            key={b.id} type="button" data-testid={`branch-open-${b.id}`}
            onClick={() => navigate(`/branches/${b.id}`)}
            style={{ animationDelay: `${idx * 40}ms` }}
            className="group animate-rise-in text-start rounded-3xl bg-background p-5 shadow-neu-sm hover:shadow-neu hover:-translate-y-0.5 transition-[box-shadow,transform] duration-300 focus:outline-none focus:ring-2 focus:ring-ring"
            data-testid-card={`branch-card-${b.id}`}
          >
            <div className="flex items-start gap-3">
              <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-white shadow-neu-sm ${meta.color}`}>
                {b.contact?.logo_url ? (
                  <img src={fileUrl(b.contact.logo_url)} alt="" data-testid={`branch-logo-${b.id}`}
                    className="h-12 w-12 rounded-full object-contain" />
                ) : <EntityIcon className="h-6 w-6" strokeWidth={2.4} />}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-lg font-bold group-hover:text-primary">{b.name}</p>
                <p className="truncate text-xs text-muted-foreground">{localName(b, b.address)}</p>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary shadow-neu-in" data-testid={`branch-entity-badge-${b.id}`}>
                    <EntityIcon className="h-2.5 w-2.5" />
                    {t(`branches.entity.${b.entity_type || "branch"}`)}
                  </span>
                  {b.is_head_office ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-black text-white shadow-neu-sm" data-testid={`branch-head-office-${b.id}`}>
                      <Home className="h-2.5 w-2.5" /> {t("branches.headOffice")}
                    </span>
                  ) : null}
                </div>
              </div>
              <MapPin className="h-5 w-5 shrink-0 text-primary" />
            </div>

            <dl className="mt-4 grid grid-cols-2 gap-3 font-mono text-xs">
              <Item label={t("branches.radius")} value={b.geofence_radius_m ? `${b.geofence_radius_m} ${t("app.meters")}` : "—"} />
              <Item label={t("branches.workStart")} value={b.work_start && b.work_end ? `${b.work_start} → ${b.work_end}` : "—"} pop />
              <Item label={t("branches.timezoneAuto")} value={b.timezone_name || `UTC+${(b.tz_offset_minutes || 0) / 60}`} />
              <Item label={t("branches.payrollDay")} value={b.payroll?.payroll_day ?? "—"} />
              <Item label={t("branches.weekendDays")}
                value={(b.weekend_days || []).map((d) => t(`days.${DAYS[d]}`)).join(" · ") || "—"} />
              <Item label={t("branches.allowWeekendWork")} value={b.allow_weekend_work ? t("app.yes") : t("app.no")} />
            </dl>

            <p className="mt-4 pt-4 text-[11px] font-bold text-primary">
              {t("branches.openCard")}
            </p>
          </button>
        );
      })}
    </div>
  );
};

const CARD_3D = {
  boxShadow:
    "0 14px 28px rgba(0,0,0,0.32), 0 8px 12px rgba(0,0,0,0.20),"
    + " inset 0 1.5px 0 rgba(255,255,255,0.28), inset 0 -4px 10px rgba(0,0,0,0.18)",
};

const Item = ({ label, value, pop }) => (
  <div>
    <dt className="text-[10px] tracking-normal text-muted-foreground">{label}</dt>
    <dd
      className={`mt-0.5 font-semibold ${pop ? "rounded-xl bg-background px-3 py-2 text-primary" : ""}`}
      style={pop ? CARD_3D : undefined}
    >
      {value}
    </dd>
  </div>
);
